/**
 * Wealth Tracking Insights Analyzer
 * Analyzes wealth snapshots to generate AI-powered insights and recommendations
 */

/**
 * Analyze wealth snapshots and generate insights
 * @param {Array} snapshots - Array of wealth snapshots (sorted chronologically)
 * @returns {Object} Insights object with recommendations and analysis
 */
export const analyzeWealthSnapshots = (snapshots) => {
  if (!snapshots || snapshots.length === 0) {
    return {
      insights: [],
      recommendations: [],
      score: null,
      trends: null
    };
  }

  const insights = [];
  const recommendations = [];

  // Sort snapshots chronologically
  const sorted = [...snapshots].sort((a, b) => a.month.localeCompare(b.month));
  const latestSnapshot = sorted[sorted.length - 1];

  // Calculate trends
  const trends = calculateTrends(sorted);

  // Analyze net worth growth
  analyzeNetWorthGrowth(sorted, insights, recommendations, trends);

  // Analyze savings rate
  analyzeSavingsRate(sorted, insights, recommendations, trends);

  // Analyze market performance
  analyzeMarketPerformance(sorted, insights, recommendations, trends);

  // Analyze debt paydown
  analyzeDebtPaydown(sorted, insights, recommendations, trends);

  // Analyze consistency
  analyzeConsistency(sorted, insights, recommendations);

  // Calculate overall score
  const score = calculateOverallScore(insights, trends);

  return {
    insights,
    recommendations: recommendations.sort((a, b) => {
      const priorityOrder = { high: 0, medium: 1, low: 2 };
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    }),
    score,
    trends
  };
};

/**
 * Calculate key trends from snapshots
 */
const calculateTrends = (snapshots) => {
  if (snapshots.length < 2) return null;

  const first = snapshots[0];
  const latest = snapshots[snapshots.length - 1];

  // ✓ FIXED: Use snapshot.changes object for aggregated values (with fallback to top-level for backward compatibility)
  const totalContributions = snapshots.reduce((sum, s) =>
    sum + (s.changes?.newContributions || s.totalContributions || 0), 0
  );

  const totalMarketGrowth = snapshots.reduce((sum, s) =>
    sum + (s.changes?.marketGrowth || s.totalMarketGrowth || 0), 0
  );

  const totalWithdrawals = snapshots.reduce((sum, s) =>
    sum + (s.changes?.withdrawals || s.totalWithdrawals || 0), 0
  );

  const totalDebtPaydown = snapshots.reduce((sum, s) =>
    sum + (s.changes?.liabilityReduction || s.totalLiabilityReduction || 0), 0
  );

  const netWorthChange = latest.netWorth - first.netWorth;
  const timeframeMonths = snapshots.length;
  const avgMonthlyContributions = totalContributions / timeframeMonths;
  const avgMonthlyGrowth = totalMarketGrowth / timeframeMonths;

  // Calculate annualized return
  const avgAssets = snapshots.reduce((sum, s) => sum + s.totalAssets, 0) / snapshots.length;
  const annualizedReturn = avgAssets > 0
    ? ((totalMarketGrowth / avgAssets) / timeframeMonths) * 12 * 100
    : 0;

  return {
    netWorthChange,
    netWorthChangePercent: first.netWorth > 0 ? (netWorthChange / first.netWorth) * 100 : 0,
    totalContributions,
    totalMarketGrowth,
    totalWithdrawals,
    totalDebtPaydown,
    avgMonthlyContributions,
    avgMonthlyGrowth,
    timeframeMonths,
    annualizedReturn,
    contributionToGrowthRatio: totalContributions > 0
      ? totalMarketGrowth / totalContributions
      : 0
  };
};

/**
 * Analyze net worth growth
 */
const analyzeNetWorthGrowth = (snapshots, insights, recommendations, trends) => {
  if (!trends) return;

  const { netWorthChange, netWorthChangePercent, timeframeMonths } = trends;

  if (netWorthChange > 0) {
    const monthlyGrowthRate = (netWorthChangePercent / timeframeMonths).toFixed(1);
    const annualizedGrowthRate = (monthlyGrowthRate * 12).toFixed(1);

    insights.push({
      type: 'positive',
      category: 'Growth',
      title: 'Net Worth Increasing',
      description: `Your net worth has grown by ${netWorthChangePercent.toFixed(1)}% over ${timeframeMonths} months (${annualizedGrowthRate}% annualized). Keep up the great work!`,
      icon: 'TrendingUp'
    });

    if (annualizedGrowthRate < 5) {
      recommendations.push({
        priority: 'medium',
        category: 'Growth',
        title: 'Accelerate Wealth Building',
        description: 'Your growth rate is positive but modest. Consider increasing contributions or reviewing investment allocation.',
        actionItems: [
          'Increase monthly contributions by 10-20%',
          'Review investment allocation for growth potential',
          'Look for opportunities to reduce fees'
        ]
      });
    }
  } else if (netWorthChange < 0) {
    insights.push({
      type: 'warning',
      category: 'Growth',
      title: 'Net Worth Declining',
      description: `Your net worth has decreased by ${Math.abs(netWorthChangePercent).toFixed(1)}% over ${timeframeMonths} months. Let's identify improvement opportunities.`,
      icon: 'TrendingDown'
    });

    recommendations.push({
      priority: 'high',
      category: 'Growth',
      title: 'Reverse Wealth Decline',
      description: 'Your net worth is trending down. Immediate action needed to get back on track.',
      actionItems: [
        'Reduce or eliminate withdrawals',
        'Increase savings contributions',
        'Review spending and create a budget',
        'Consider if market losses require portfolio rebalancing'
      ]
    });
  }
};

/**
 * Analyze savings rate
 */
const analyzeSavingsRate = (snapshots, insights, recommendations, trends) => {
  if (!trends || trends.timeframeMonths < 2) return;

  const { avgMonthlyContributions, totalContributions } = trends;

  if (avgMonthlyContributions > 1000) {
    insights.push({
      type: 'positive',
      category: 'Savings',
      title: 'Strong Savings Discipline',
      description: `You're averaging ${formatCurrency(avgMonthlyContributions)}/month in contributions. Excellent savings habit!`,
      icon: 'Wallet'
    });
  } else if (avgMonthlyContributions > 0) {
    insights.push({
      type: 'neutral',
      category: 'Savings',
      title: 'Building Savings Habit',
      description: `You're averaging ${formatCurrency(avgMonthlyContributions)}/month in contributions. There's room to increase this.`,
      icon: 'Wallet'
    });

    recommendations.push({
      priority: 'medium',
      category: 'Savings',
      title: 'Increase Savings Rate',
      description: 'Boost your monthly contributions to accelerate wealth building.',
      actionItems: [
        'Aim to save at least 15-20% of income',
        'Automate contributions to make saving effortless',
        'Look for expense reduction opportunities'
      ]
    });
  } else {
    insights.push({
      type: 'warning',
      category: 'Savings',
      title: 'Limited Contributions Detected',
      description: 'Your wealth growth is primarily from market gains. Regular contributions are essential for long-term success.',
      icon: 'AlertTriangle'
    });

    recommendations.push({
      priority: 'high',
      category: 'Savings',
      title: 'Start Regular Contributions',
      description: 'Begin making consistent monthly contributions to build wealth systematically.',
      actionItems: [
        'Set up automatic monthly transfers',
        'Start with 10% of income and increase over time',
        'Create an emergency fund first (3-6 months expenses)'
      ]
    });
  }
};

/**
 * Analyze market performance
 */
const analyzeMarketPerformance = (snapshots, insights, recommendations, trends) => {
  if (!trends || trends.timeframeMonths < 2) return;

  const { totalMarketGrowth, annualizedReturn, contributionToGrowthRatio } = trends;

  if (annualizedReturn > 8) {
    insights.push({
      type: 'positive',
      category: 'Investments',
      title: 'Strong Investment Returns',
      description: `Your investments are generating ${annualizedReturn.toFixed(1)}% annualized returns, above historical market averages.`,
      icon: 'LineChart'
    });
  } else if (annualizedReturn > 0) {
    insights.push({
      type: 'neutral',
      category: 'Investments',
      title: 'Moderate Investment Returns',
      description: `Your investments returned ${annualizedReturn.toFixed(1)}% annualized. This is below typical market returns of 8-10%.`,
      icon: 'LineChart'
    });

    if (annualizedReturn < 5) {
      recommendations.push({
        priority: 'medium',
        category: 'Investments',
        title: 'Optimize Investment Strategy',
        description: 'Your returns are lagging market averages. Consider reviewing your allocation.',
        actionItems: [
          'Review asset allocation and diversification',
          'Consider low-cost index funds',
          'Ensure you\'re not holding too much cash',
          'Review and minimize investment fees'
        ]
      });
    }
  } else if (totalMarketGrowth < 0) {
    insights.push({
      type: 'warning',
      category: 'Investments',
      title: 'Investment Losses',
      description: `Your investments have declined by ${formatCurrency(Math.abs(totalMarketGrowth))} over this period.`,
      icon: 'TrendingDown'
    });

    recommendations.push({
      priority: 'medium',
      category: 'Investments',
      title: 'Review Investment Strategy',
      description: 'Market downturns are normal, but ensure your strategy aligns with your goals and risk tolerance.',
      actionItems: [
        'Don\'t panic sell - stay invested for long-term',
        'Ensure diversification across asset classes',
        'Consider if allocation matches your risk tolerance',
        'Look for buying opportunities during downturns'
      ]
    });
  }

  // Analyze contribution vs growth ratio
  if (contributionToGrowthRatio > 0.5 && totalMarketGrowth > 0) {
    insights.push({
      type: 'positive',
      category: 'Balance',
      title: 'Great Balance of Saving & Investing',
      description: 'Your market gains are complementing your contributions nicely. Both are working together to build wealth.',
      icon: 'Target'
    });
  }
};

/**
 * Analyze debt paydown
 */
const analyzeDebtPaydown = (snapshots, insights, recommendations, trends) => {
  if (!trends) return;

  const { totalDebtPaydown, avgMonthlyContributions } = trends;
  const latestSnapshot = snapshots[snapshots.length - 1];

  if (totalDebtPaydown > 0) {
    insights.push({
      type: 'positive',
      category: 'Debt',
      title: 'Debt Reduction Progress',
      description: `You've paid down ${formatCurrency(totalDebtPaydown)} in debt principal. Every dollar paid reduces your liabilities!`,
      icon: 'CreditCard'
    });
  }

  // If they have high debt relative to assets
  if (latestSnapshot.totalLiabilities > 0) {
    const debtToAssetRatio = (latestSnapshot.totalLiabilities / latestSnapshot.totalAssets) * 100;

    if (debtToAssetRatio > 50) {
      insights.push({
        type: 'warning',
        category: 'Debt',
        title: 'High Debt-to-Asset Ratio',
        description: `Your debts represent ${debtToAssetRatio.toFixed(0)}% of your assets. Focus on debt reduction can accelerate net worth growth.`,
        icon: 'AlertCircle'
      });

      recommendations.push({
        priority: 'high',
        category: 'Debt',
        title: 'Prioritize Debt Paydown',
        description: 'High debt levels are limiting your wealth building potential.',
        actionItems: [
          'Consider debt avalanche method (highest interest first)',
          'Look for refinancing opportunities to lower rates',
          'Allocate windfall income to debt reduction',
          'Create a debt payoff timeline'
        ]
      });
    } else if (debtToAssetRatio > 25) {
      recommendations.push({
        priority: 'medium',
        category: 'Debt',
        title: 'Continue Debt Reduction',
        description: 'You\'re managing debt well. Keep making progress to reduce liabilities.',
        actionItems: [
          'Maintain current debt paydown pace',
          'Avoid taking on new high-interest debt',
          'Consider extra payments when possible'
        ]
      });
    }
  }
};

/**
 * Analyze consistency of tracking
 */
const analyzeConsistency = (snapshots, insights, recommendations) => {
  if (snapshots.length < 3) {
    recommendations.push({
      priority: 'low',
      category: 'Tracking',
      title: 'Build Tracking Consistency',
      description: 'The more months you track, the better insights you\'ll get.',
      actionItems: [
        'Create snapshots monthly for best results',
        'Set a calendar reminder on the 1st of each month',
        'Track for at least 6-12 months to see meaningful trends'
      ]
    });
  } else if (snapshots.length >= 6) {
    insights.push({
      type: 'positive',
      category: 'Tracking',
      title: 'Consistent Wealth Tracking',
      description: `You've tracked ${snapshots.length} months! This consistency enables powerful insights and trend analysis.`,
      icon: 'Calendar'
    });
  }
};

/**
 * Calculate overall wealth health score (0-100)
 */
const calculateOverallScore = (insights, trends) => {
  if (!trends) return null;

  let score = 50; // Start at neutral

  // Positive net worth change
  if (trends.netWorthChange > 0) {
    score += Math.min(trends.netWorthChangePercent / 2, 20);
  } else {
    score -= Math.min(Math.abs(trends.netWorthChangePercent) / 2, 20);
  }

  // Savings contributions
  if (trends.avgMonthlyContributions > 1000) score += 15;
  else if (trends.avgMonthlyContributions > 500) score += 10;
  else if (trends.avgMonthlyContributions > 0) score += 5;

  // Investment returns
  if (trends.annualizedReturn > 8) score += 15;
  else if (trends.annualizedReturn > 5) score += 10;
  else if (trends.annualizedReturn > 0) score += 5;
  else if (trends.annualizedReturn < -5) score -= 10;

  // Debt paydown
  if (trends.totalDebtPaydown > 0) score += 10;

  return Math.max(Math.min(Math.round(score), 100), 0);
};

/**
 * Format currency for display
 */
const formatCurrency = (amount) => {
  const absAmount = Math.abs(amount);
  if (absAmount >= 1000000) {
    return `$${(absAmount / 1000000).toFixed(1)}M`;
  } else if (absAmount >= 1000) {
    return `$${(absAmount / 1000).toFixed(1)}K`;
  }
  return `$${absAmount.toFixed(0)}`;
};
